import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRightIcon, ChartBarIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Button, Card, Container, EmptyState, InfoBox, Loading, PageHeader, RowAction, Table, Tag } from "../../ui";
import { TYPE_LABELS, destinationOf, formatDuration, formatKigaliDate, offeredToStudents, placeOf } from "../model";
import CallListItem, { toggleIn } from "../../calls/CallListItem";
import ProgressDialog from "./ProgressDialog";
import EligibilityNote, { PROFILE_PATH } from "./EligibilityNote";
import ProfileForm from "./ProfileForm";
import { closesIn, studentStatusOf, useAcademicProfile, useMyApplications, useOpenScholarships } from "./model";
import { abroadApplicationPath, abroadCallPath, useMyAbroadApplications, useOpenAbroadCalls } from "../../abroadLoans/student";

const programmeItemOf = (p) => ({
  key: p.id ?? `${p.name}-${p.institutionName}`,
  name: p.name,
  meta: [placeOf(p), p.level?.name ?? p.levelName, formatDuration(p.durationMonths)].filter(Boolean).join(" · "),
});

// The way into one call: the student's application to it when there is one, else the call itself
function OpenButton({ to, applied }) {
  const navigate = useNavigate();
  return (
    <Button size="sm" variant={applied ? "outline" : "solid"} icon={<ArrowRightIcon className="h-4 w-4" />} onClick={() => navigate(to)}>
      {applied ? "View" : "View and apply"}
    </Button>
  );
}

function ScholarshipItem({ scholarship, expanded, onToggle }) {
  const status = scholarship.myApplication && studentStatusOf(scholarship.myApplication.studentStatus);
  return (
    <CallListItem
      id={scholarship.id}
      name={scholarship.name}
      tag={status && <Tag color={status.color}>{status.label}</Tag>}
      place={`${destinationOf(scholarship)} · ${scholarship.sponsor?.name ?? "HEC"} · ${TYPE_LABELS[scholarship.type] ?? scholarship.type}`}
      closing={closesIn(scholarship.closesAt)}
      summary={scholarship.publicSummary || scholarship.description}
      extra={!scholarship.myApplication && !scholarship.myEligibility?.profileRequired && <EligibilityNote eligibility={scholarship.myEligibility} compact />}
      programmes={scholarship.programmes.map(programmeItemOf)}
      expanded={expanded}
      onToggle={() => onToggle(scholarship.id)}
      action={<OpenButton to={`/scholarships/${scholarship.id}`} applied={Boolean(scholarship.myApplication)} />}
    />
  );
}

// An open abroad loan call (abroad-loans API), with the student's application to it when there is one
function LoanCallItem({ call, application, expanded, onToggle }) {
  const status = application && studentStatusOf(application.studentStatus);
  return (
    <CallListItem
      id={call.id}
      name={call.name}
      tag={status && <Tag color={status.color}>{status.label}</Tag>}
      place="Where you are admitted · HEC · Abroad loan"
      closing={closesIn(call.closesAt)}
      summary={call.publicSummary || call.description}
      programmes={call.programmes.map(({ programme }) => programmeItemOf(programme))}
      expanded={expanded}
      onToggle={() => onToggle(call.id)}
      action={<OpenButton to={abroadCallPath(call.id)} applied={Boolean(application)} />}
    />
  );
}

// Every open scholarship and abroad loan call; scholarships the student's profile does not meet say so
function Scholarships() {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = toggleIn(setExpanded);
  const { data, isPending, isError, refetch } = useOpenScholarships();
  const { data: loanCalls = [], isPending: loansPending, isError: loansFailed } = useOpenAbroadCalls();
  const { data: loanApplications = [] } = useMyAbroadApplications();
  if (isPending || loansPending) return <Loading label="Finding scholarships for you…" />;
  if (isError) return <EmptyState title="Could not load scholarships" onRefresh={refetch} />;
  const open = data.content.filter((s) => offeredToStudents(s.type));
  if (!open.length && !loanCalls.length)
    return <EmptyState title={loansFailed ? "Could not load the abroad loans" : "No open scholarships right now"} description="New ones are published during the year. Check back soon." />;
  const loanApplicationTo = (callId) => loanApplications.find((a) => a.callId === callId && a.studentStatus !== "WITHDRAWN");
  const profileRequired = open.some((s) => s.myEligibility?.profileRequired);
  return (
    <>
      {profileRequired && (
        <InfoBox>
          <span>
            Fill in your{" "}
            <Link to={PROFILE_PATH} className="font-semibold underline">
              academic profile
            </Link>{" "}
            once: it is used for every application and shows which scholarships you can apply for.
          </span>
        </InfoBox>
      )}
      <div className="flex flex-col gap-3">
        {open.map((s) => (
          <ScholarshipItem key={s.id} scholarship={s} expanded={expanded.has(s.id)} onToggle={toggle} />
        ))}
        {loanCalls.map((c) => (
          <LoanCallItem key={c.id} call={c} application={loanApplicationTo(c.id)} expanded={expanded.has(c.id)} onToggle={toggle} />
        ))}
      </div>
    </>
  );
}

// Scholarship and abroad loan applications as one row shape
const scholarshipRow = (a) => ({
  id: a.id,
  name: a.scholarship.name,
  type: TYPE_LABELS[a.scholarship.type] ?? a.scholarship.type,
  programme: [a.programme.name, a.programme.institutionName].filter(Boolean).join(", "),
  closesAt: a.scholarship.closesAt,
  submittedAt: a.submittedAt,
  studentStatus: a.studentStatus,
  path: `/my-applications/${a.id}`,
});
const loanRow = (a) => ({
  id: a.id,
  name: a.callName,
  type: "Abroad loan",
  programme: a.programmeName,
  closesAt: a.closesAt,
  submittedAt: a.submittedAt,
  studentStatus: a.studentStatus,
  path: abroadApplicationPath(a.id),
  abroad: true,
});

function Applications() {
  const navigate = useNavigate();
  const [progressOf, setProgressOf] = useState(null);
  const { data = [], isPending, isError } = useMyApplications();
  const { data: loans = [], isPending: loansPending, isError: loansFailed } = useMyAbroadApplications();
  const rows = [...data.map(scholarshipRow), ...loans.map(loanRow)];
  const open = (row) => navigate(row.path);
  const columns = [
    { key: "name", label: "Scholarship", accessor: (a) => a.name, render: (a) => <span className="font-medium text-slate-800">{a.name}</span> },
    { key: "type", label: "Type", accessor: (a) => a.type },
    { key: "programme", label: "Programme", accessor: (a) => a.programme },
    { key: "closesAt", label: "Deadline", accessor: (a) => formatKigaliDate(a.closesAt), sortValue: (a) => a.closesAt },
    { key: "submittedAt", label: "Submitted", accessor: (a) => formatKigaliDate(a.submittedAt), sortValue: (a) => a.submittedAt },
    {
      key: "status",
      label: "Status",
      accessor: (a) => studentStatusOf(a.studentStatus).label,
      // Opens the application's progress
      render: (a) => (
        <button type="button" onClick={() => setProgressOf(a)} title="See the progress" className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-brand hover:opacity-80">
          <Tag color={studentStatusOf(a.studentStatus).color}>
            <span className="flex items-center gap-1">
              {studentStatusOf(a.studentStatus).label}
              <ChartBarIcon className="h-3.5 w-3.5" />
            </span>
          </Tag>
        </button>
      ),
    },
    {
      key: "open",
      label: "",
      exportable: false,
      render: (a) => <RowAction Icon={EyeIcon} label={a.studentStatus === "DRAFT" ? "Continue" : "View"} onClick={() => open(a)} />,
    },
  ];
  return (
    <>
      <Table cardView viewKey="student-portal"
        columns={columns}
        data={rows}
        isLoading={isPending || loansPending}
        searchable={false}
        exportable={false}
        emptyTitle={isError || loansFailed ? "Could not load your applications." : "You have not applied for a scholarship yet"}
      />
      {progressOf && <ProgressDialog row={progressOf} onClose={() => setProgressOf(null)} />}
    </>
  );
}

// The student's home: open sponsored scholarships and abroad loans. Links to the former
// "My applications" tab land on their own page now.
export default function StudentPortalPage() {
  const [params] = useSearchParams();
  if (params.get("tab") === "applications") return <Navigate to="/my-applications" replace />;
  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Scholarships" subtitle="Partner scholarships and abroad loans open for applications" />
      <Scholarships />
    </Container>
  );
}

// Every application the student started, whatever its type
export function MyApplicationsPage() {
  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="My applications" subtitle="Follow each application from draft to decision" />
      <Applications />
    </Container>
  );
}

// The student's studies, kept once for every application; eligibility is checked against them
export function AcademicProfilePage() {
  const navigate = useNavigate();
  const { data: profile, isPending, isError, refetch } = useAcademicProfile();
  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Academic profile" subtitle="Your studies, used for every application and to show which scholarships you can apply for" />
      {isPending ? (
        <Loading label="Loading your profile…" />
      ) : isError ? (
        <EmptyState title="Could not load your profile" onRefresh={refetch} />
      ) : (
        <Card>
          {!profile && <InfoBox className="mb-4">You have no academic profile yet.</InfoBox>}
          <ProfileForm profile={profile} onSaved={() => navigate("/studentPortal")} />
          {profile && (
            <p className="mt-4 text-xs text-slate-500">
              Applications you already submitted keep the studies they were sent with.
            </p>
          )}
        </Card>
      )}
    </Container>
  );
}
